/**
 * CircleTool: Interactive tool for drawing circle shapes
 * Feature: 049-critique-photo-markup
 *
 * Click-drag interaction: Center point on mouse down, radius expands to mouse up
 */

import { useState, useRef, useCallback } from 'react';
import type { CircleShape, ShapeStyle, MarkupShape } from '@/types/markup.types';
import { pixelToPercentage, transformShapeForViewBox } from '@/utils/markupRenderer';
import { validateCircle, isAccidentalClick } from '@/utils/markupValidation';
import { useMarkupDrag } from '@/hooks/useMarkupDrag';
import logger from '@/utils/logger';

interface CircleToolProps {
  currentStyle: ShapeStyle;
  onShapeComplete: (shape: CircleShape) => void;
  onValidationError: (message: string) => void;
  onToolClick: (x: number, y: number) => number | null;
  onShapeClick: (shapeNumber: number) => void;
  selectedShapeId: number | null;
  shapes: MarkupShape[];
  onShapeMove: (shapeNumber: number, shape: MarkupShape) => void;
  containerRef: React.RefObject<HTMLDivElement>;
  photoAspectRatio?: number;
}

export function CircleTool({
  currentStyle,
  onShapeComplete,
  onValidationError,
  onToolClick: _onToolClick, // Not used - hook handles hit detection internally
  onShapeClick,
  selectedShapeId,
  shapes,
  onShapeMove,
  containerRef,
  photoAspectRatio = 1,
}: CircleToolProps) {
  const viewBoxHeight = 100 / photoAspectRatio;
  const viewBox = `0 0 100 ${viewBoxHeight}`;
  const [isDrawing, setIsDrawing] = useState(false);
  const [previewShape, setPreviewShape] = useState<CircleShape | null>(null);
  const startPointRef = useRef<{ x: number; y: number } | null>(null);

  // Use shared drag handler for existing shapes and handles
  const {
    cursor: dragCursor,
    handleMouseDown: dragMouseDown,
    handleMouseMove: dragMouseMove,
    handleMouseUp: dragMouseUp,
    handleMouseLeave: dragMouseLeave,
  } = useMarkupDrag({
    shapes,
    selectedShapeId, // Pass through so hook can detect handle clicks
    onShapeClick, // Pass through to hook for direct shape selection
    onShapeMove,
    containerRef,
    photoAspectRatio,
    readOnly: false,
  });

  // Use drag cursor with fallback to crosshair for drawing
  const cursor = dragCursor ?? 'crosshair';

  /**
   * Get relative coordinates within container (percentage)
   * Accounts for object-contain letterboxing/pillarboxing
   */
  const getRelativeCoords = useCallback(
    (e: React.MouseEvent): { x: number; y: number } => {
      if (!containerRef.current) return { x: 0, y: 0 };

      const rect = containerRef.current.getBoundingClientRect();
      const containerWidth = rect.width;
      const containerHeight = rect.height;
      const containerAspectRatio = containerWidth / containerHeight;

      // Calculate actual photo dimensions within container (object-contain behavior)
      let photoWidth, photoHeight, offsetX, offsetY;

      if (photoAspectRatio > containerAspectRatio) {
        // Photo is wider - letterboxing on top/bottom
        photoWidth = containerWidth;
        photoHeight = containerWidth / photoAspectRatio;
        offsetX = 0;
        offsetY = (containerHeight - photoHeight) / 2;
      } else {
        // Photo is taller - pillarboxing on left/right
        photoHeight = containerHeight;
        photoWidth = containerHeight * photoAspectRatio;
        offsetX = (containerWidth - photoWidth) / 2;
        offsetY = 0;
      }

      // Convert mouse position to photo-relative coordinates
      const pixelX = e.clientX - rect.left - offsetX;
      const pixelY = e.clientY - rect.top - offsetY;

      // Convert to percentage (0-100 for X, 0-viewBoxHeight for Y)
      const result = pixelToPercentage(pixelX, pixelY, photoWidth, photoHeight, photoAspectRatio);
      return result;
    },
    [containerRef, photoAspectRatio]
  );

  /**
   * Calculate circle radius from center to current point
   */
  const calculateRadius = useCallback(
    (center: { x: number; y: number }, current: { x: number; y: number }): number => {
      const dx = current.x - center.x;
      const dy = current.y - center.y;
      return Math.sqrt(dx * dx + dy * dy);
    },
    []
  );

  /**
   * Handle mouse down - check for existing shape/handle first, then start drawing circle
   */
  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      // First, check if clicking on existing shape or handle - let shared handler manage it
      const handledByDrag = dragMouseDown(e);
      if (handledByDrag) {
        logger.debug('CircleTool', 'Click handled by drag system');
        return;
      }

      // Not clicking on shape/handle - start drawing new circle
      const center = getRelativeCoords(e);
      startPointRef.current = center;
      setIsDrawing(true);

      // Create preview shape with zero radius
      const preview: CircleShape = {
        type: 'circle',
        number: 0, // Will be assigned when added to shapes array
        strokeWidth: currentStyle.strokeWidth,
        strokeColor: currentStyle.strokeColor,
        strokeDasharray: currentStyle.strokeDasharray,
        geometry: {
          cx: center.x,
          cy: center.y,
          r: 0,
        },
      };

      setPreviewShape(preview);
      logger.debug('CircleTool', 'Started drawing circle at', center);
    },
    [dragMouseDown, getRelativeCoords, currentStyle]
  );

  /**
   * Handle mouse move - update preview circle or delegate to drag handler
   */
  const handleMouseMove = useCallback(
    (e: React.MouseEvent) => {
      // Delegate to drag handler (handles shape/handle dragging)
      dragMouseMove(e);

      // Handle drawing new shape
      if (!isDrawing || !startPointRef.current || !previewShape) return;

      const current = getRelativeCoords(e);
      const radius = calculateRadius(startPointRef.current, current);

      setPreviewShape((prev) =>
        prev
          ? {
              ...prev,
              geometry: {
                ...prev.geometry,
                r: radius,
              },
            }
          : null
      );
    },
    [isDrawing, previewShape, dragMouseMove, getRelativeCoords, calculateRadius]
  );

  /**
   * Handle mouse up - finalize circle or complete drag
   */
  const handleMouseUp = useCallback(() => {
    // Delegate to drag handler (completes any active drag)
    dragMouseUp();

    // Complete drawing new shape
    if (!previewShape) return;

    // Check if this was just a click (deselect any selected shape)
    if (isAccidentalClick(previewShape)) {
      logger.debug('CircleTool', 'Click detected - deselecting');
      onValidationError('click'); // Triggers deselection in editor
      setIsDrawing(false);
      setPreviewShape(null);
      startPointRef.current = null;
      return;
    }

    // Validate circle meets minimum size (pass viewBoxHeight for aspect-ratio-adjusted validation)
    const validation = validateCircle(previewShape.geometry, viewBoxHeight);
    if (!validation.valid) {
      logger.warn('CircleTool', 'Circle validation failed:', validation.error);
      onValidationError(validation.error || 'Circle too small, try again');
    } else {
      logger.info('CircleTool', 'Circle created:', previewShape.geometry);
      onShapeComplete(previewShape);
    }

    // Reset state
    setIsDrawing(false);
    setPreviewShape(null);
    startPointRef.current = null;
  }, [previewShape, dragMouseUp, onShapeComplete, onValidationError, viewBoxHeight]);

  /**
   * Handle mouse leave - cancel drawing and clear hover state
   */
  const handleMouseLeaveWrapper = useCallback(() => {
    handleMouseUp(); // Cancel drawing if active
    dragMouseLeave(); // Clear hover state
  }, [handleMouseUp, dragMouseLeave]);

  return (
    <>
      {/* Transparent overlay to capture mouse events */}
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          cursor,
          zIndex: 15,
        }}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseLeaveWrapper} // Cancel drawing and clear hover state
      />

      {/* Preview circle while drawing */}
      {previewShape && previewShape.geometry.r > 0 && (() => {
        // BUG FIX (#480): Transform preview shape from image space to viewBox space
        const transformedPreview = transformShapeForViewBox(previewShape, photoAspectRatio);

        return (
          <svg
            viewBox={viewBox}
            preserveAspectRatio="xMidYMid meet"
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              pointerEvents: 'none',
              zIndex: 16,
            }}
          >
            <circle
              cx={transformedPreview.geometry.cx}
              cy={transformedPreview.geometry.cy}
              r={transformedPreview.geometry.r}
              stroke={transformedPreview.strokeColor}
              strokeWidth={transformedPreview.strokeWidth}
              strokeDasharray={transformedPreview.strokeDasharray}
              fill="none"
              vectorEffect="non-scaling-stroke"
              opacity={0.5}
            />
          </svg>
        );
      })()}
    </>
  );
}
